import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Maximize2, X } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { modelsApi } from "@/api/models";
import { Button } from "@/components/ui/button";
import { Field, TextInput, Textarea, Select } from "./form-primitives";
import { HighlightedTextarea } from "./highlighted-textarea";
import { agentRoleLabel } from "@/lib/agent-display";
import { cn } from "@/lib/utils";

export function AgentConfig({
  data,
  onChange,
  readOnly,
  variables,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  variables?: { value: string; label: string; hint?: string }[];
}) {
  const { tenantId } = useTenant();
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [view, setView] = useState<"config" | "settings">("config");
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const { data: availability } = useQuery({
    queryKey: ["models", "availability", tenantId],
    queryFn: () => modelsApi.getAvailability(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });

  const models = (availability?.models ?? []).filter(
    (m) => m.enabled && (m.category === "chat" || m.category === "reasoning"),
  );
  const modelOptions = (defaultLabel: string) => [
    { value: "", label: defaultLabel },
    ...models.map((m) => ({
      value: m.modelId,
      label: m.accessReady ? m.displayName : `${m.displayName} (no credentials)`,
    })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-border">
        {(
          [
            { id: "config", label: "Configuration" },
            { id: "settings", label: "Settings" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setView(t.id)}
            className={cn(
              "relative px-3 py-1.5 text-[12px] font-medium transition-colors",
              view === t.id
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary",
            )}
          >
            {t.label}
            {view === t.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" />
            )}
          </button>
        ))}
      </div>

      {view === "config" && (
        <div className="space-y-4">
      <Field label="Label">
        <TextInput
          value={(data.label as string) ?? ""}
          onChange={(v) => update({ label: v })}
          placeholder="e.g. Research Agent"
          readOnly={readOnly}
        />
      </Field>

      <Field label="Agent">
        <Select
          value={(data.agentId as string) ?? ""}
          onChange={(v) => update({ agentId: v || undefined })}
          disabled={readOnly}
          options={[
            { value: "", label: agents.length > 0 ? "— select an agent —" : "No agents available" },
            ...agents.map((a) => {
              const subtitle = agentRoleLabel(a);
              return {
                value: a.id,
                label: subtitle ? `${a.name} · ${subtitle}` : a.name,
              };
            }),
          ]}
        />
      </Field>

      <Field label="Model">
        <Select
          value={(data.model as string) ?? ""}
          onChange={(v) => update({ model: v || undefined })}
          disabled={readOnly}
          options={modelOptions("Agent default")}
        />
        <p className="mt-1 text-[11px] text-text-muted">
          Overrides the agent's configured model for this step only.
        </p>
      </Field>

      <Field label="Backup model">
        <Select
          value={(data.fallbackModel as string) ?? ""}
          onChange={(v) => update({ fallbackModel: v || undefined })}
          disabled={readOnly}
          options={modelOptions("Agent default (final fallback)")}
        />
        <p className="mt-1 text-[11px] text-text-muted">
          Used when the primary model fails or the step is retried. The
          agent's own model is always the final fallback.
        </p>
      </Field>

      <Field label="Prompt template">
        {/* Expand button (lower-right) opens a large popup so the full prompt
            is readable without dragging the small textarea taller. */}
        <div className="relative">
          <HighlightedTextarea
            value={(data.promptTemplate as string) ?? ""}
            onChange={(v) => update({ promptTemplate: v })}
            placeholder="What should this agent do when the workflow reaches this step?"
            readOnly={readOnly}
            rows={6}
            variables={variables}
          />
          <button
            type="button"
            onClick={() => setPromptExpanded(true)}
            aria-label="Expand prompt template"
            title="Expand"
            className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded border border-border/70 bg-background/80 text-text-muted backdrop-blur-sm transition-colors hover:border-border hover:bg-surface-hover hover:text-text-primary"
          >
            <Maximize2 size={11} />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-text-muted">
          When execution lands, variables from previous steps will be available with{" "}
          <code className="font-mono">{"{{input}}"}</code> interpolation.
        </p>
      </Field>

      <Field label="Description">
        <Textarea
          value={(data.description as string) ?? ""}
          onChange={(v) => update({ description: v })}
          placeholder="Shown on the node body"
          readOnly={readOnly}
          rows={2}
        />
      </Field>
        </div>
      )}

      {view === "settings" && (
        <div className="space-y-4">
          <Field label="On no response (timeout)">
            <Select
              value={(data.onAwaitTimeout as string) ?? "fail"}
              onChange={(v) => update({ onAwaitTimeout: v })}
              disabled={readOnly}
              options={[
                { value: "fail", label: "Fail the run (default)" },
                { value: "continue", label: "Continue the workflow" },
              ]}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Only applies when this node waits for a response (e.g. after
              sending a Teams card). "Continue" resumes the workflow with{" "}
              <code className="font-mono">timed_out</code> set, so add a
              Condition branch to handle it. Nodes that do not wait are
              unaffected.
            </p>
          </Field>
        </div>
      )}

      {promptExpanded &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
            onClick={() => setPromptExpanded(false)}
          >
            <div
              className="flex max-h-[80vh] w-[min(760px,calc(100vw-3rem))] flex-col rounded-lg border border-border bg-surface shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <h3 className="text-sm font-semibold text-text-primary">
                  Prompt template{readOnly ? " (read-only)" : ""}
                </h3>
                <button
                  type="button"
                  onClick={() => setPromptExpanded(false)}
                  aria-label="Close"
                  className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
                >
                  <X size={15} />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <HighlightedTextarea
                  autoFocus
                  value={(data.promptTemplate as string) ?? ""}
                  onChange={(v) => update({ promptTemplate: v })}
                  readOnly={readOnly}
                  placeholder="What should this agent do when the workflow reaches this step?"
                  className="h-[52vh]"
                  variables={variables}
                />
                <p className="mt-2 text-[11px] text-text-muted">
                  Variables from previous steps are available with{" "}
                  <code className="font-mono">{"{{input}}"}</code> interpolation.
                </p>
              </div>
              <div className="flex justify-end border-t border-border px-4 py-2.5">
                <Button size="sm" onClick={() => setPromptExpanded(false)}>
                  Done
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
